import { ChevronRight, ArrowUp, ArrowDown, Pencil, Trash2, Bird, Star } from "lucide-react";
import { TileImg } from "../TileImg";

const IconBtn = ({ testId, label, icon: Icon, onClick, disabled, danger }) => (
  <button
    type="button"
    data-testid={testId}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className={`grid place-items-center h-8 w-8 rounded-full border border-border bg-card transition-colors duration-200 hover:bg-muted disabled:opacity-30 disabled:pointer-events-none ${danger ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);

// The flock's cover: the chosen duck (accent ring) or, until one is picked, its newest duck; empty flocks get a bird.
const FlockCover = ({ cover, testId }) => {
  const box = "h-8 w-8 sm:h-10 sm:w-10 shrink-0 rounded-xl border bg-muted/40 transition-transform duration-200 group-hover:scale-105";
  if (!cover) {
    return (
      <span data-testid={testId} data-chosen="none" className={`${box} grid place-items-center border-dashed border-border text-muted-foreground`}>
        <Bird className="h-4 w-4" />
      </span>
    );
  }
  const name = cover.name || `${cover.style || "classic"} ${cover.figure === "full" ? "full duck" : "bust"}`;
  return (
    <span data-testid={testId} data-chosen={cover.chosen ? "chosen" : "newest"} title={cover.chosen ? `Cover: ${name}` : `Newest duck: ${name}`} className="relative shrink-0">
      <TileImg tile={cover.tile} size="64" px={40} alt={`${name} (flock cover)`} testId={`${testId}-img`} className={`${box} object-cover ${cover.chosen ? "border-accent ring-2 ring-accent/40" : "border-border"}`} />
      {cover.chosen && <Star className="absolute -right-1 -top-1 h-3.5 w-3.5 fill-accent text-accent drop-shadow" aria-hidden="true" />}
    </span>
  );
};

// A flock's title bar: collapse toggle, cover + count; real flocks also get up/down, rename and delete.
export const FlockHeader = ({ flock, count, matches, open, onToggle, first, last, onUp, onDown, onRename, onDelete }) => {
  const key = flock ? flock.id : "unsorted";
  return (
    <div data-testid={`flock-header-${key}`} className="flex items-center gap-2 py-3 border-b border-border/70">
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`flock-toggle-${key}`} className="group flex flex-1 min-w-0 items-center gap-2 text-left">
        <ChevronRight className={`h-5 w-5 shrink-0 text-accent transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
        {flock && <FlockCover cover={flock.cover} testId={`flock-cover-${key}`} />}
        <span data-testid={`flock-name-${key}`} className={`font-heading text-xl sm:text-2xl font-extrabold tracking-tight truncate ${flock ? "" : "text-muted-foreground"}`}>
          {flock ? flock.name : "Unsorted"}
        </span>
        <span data-testid={`flock-count-${key}`} className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-mono2 text-muted-foreground">
          {matches != null ? `${matches} of ${count}` : `${count} duck${count === 1 ? "" : "s"}`}
        </span>
      </button>
      {flock && (
        <div className="flex items-center gap-1 shrink-0">
          <IconBtn testId={`flock-up-${key}`} label={`Move ${flock.name} up`} icon={ArrowUp} disabled={first} onClick={onUp} />
          <IconBtn testId={`flock-down-${key}`} label={`Move ${flock.name} down`} icon={ArrowDown} disabled={last} onClick={onDown} />
          <IconBtn testId={`flock-rename-${key}`} label={`Rename ${flock.name}`} icon={Pencil} onClick={onRename} />
          <IconBtn testId={`flock-delete-${key}`} label={`Delete ${flock.name}`} icon={Trash2} onClick={onDelete} danger />
        </div>
      )}
    </div>
  );
};
